import { useEffect } from 'react';
import { X } from 'lucide-react';
import PlaystoreBadge from '../../assets/playstore-badge.svg';
import IosBadge from '../../assets/appstore-badge.svg';
import { MOBILE_APP_CONFIG } from '@/config/constants';

interface AppDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
}

export default function AppDownloadModal({
  isOpen,
  onClose,
  title = 'Watch Live on Auqli',
  description = 'This livestream is available in our mobile app. Download now to watch!',
}: AppDownloadModalProps) {
  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className="relative w-full max-w-md bg-white rounded-xl shadow-2xl p-6 md:p-8 animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >

        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close modal"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>


        <div className="flex flex-col items-center text-center mt-2">

          <div className="w-20 h-20 mb-6 rounded-2xl bg-[#16783a] flex items-center justify-center">
            <svg
              className="w-12 h-12 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>


          <h2
            id="modal-title"
            className="text-2xl md:text-3xl font-semibold text-gray-900 mb-3 font-lexend"
          >
            {title}
          </h2>


          <p className="text-base md:text-lg text-gray-600 mb-8 font-lexend max-w-sm">
            {description}
          </p>

          {/* App Store Badges */}
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full">
            <a
              href={MOBILE_APP_CONFIG.APP_STORE.ANDROID}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get it on Google Play"
              className="h-[56px] sm:h-[60px] w-full sm:w-auto flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img
                src={PlaystoreBadge}
                alt="Get it on Google Play"
                className="h-full w-auto"
              />
            </a>
            <a
              href={MOBILE_APP_CONFIG.APP_STORE.IOS}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on the App Store"
              className="h-[56px] sm:h-[60px] w-full sm:w-auto flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img
                src={IosBadge}
                alt="Download on the App Store"
                className="h-full w-auto"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
